class LicenseCache {
  constructor() {
    this.cache = new Map();
    this.etags = new Map();
    this.lastRefresh = new Map();
    this.refreshInterval = 5 * 60 * 1000; // 5 minutes
    this.backgroundRefreshTimer = null;
    this.pendingRequests = new Map();
  }

  // Get cached license data with fallback
  getCachedLicense(repo) {
    const cached = this.cache.get(repo);
    if (!cached) return null;

    // Check if cache is still valid
    const now = Date.now();
    if (now - cached.timestamp < this.refreshInterval) {
      return cached.data;
    }

    // Cache expired, but return stale data while refreshing in background
    this.refreshLicenseInBackground(repo);
    return cached.data;
  }

  // Set cached license data
  setCachedLicense(repo, data, etag = null) {
    this.cache.set(repo, {
      data,
      timestamp: Date.now()
    });
    if (etag) {
      this.etags.set(repo, etag);
    }
  }

  // Batch fetch license information for multiple repositories
  async batchFetchLicenses(repos, apiUrl, idToken) {
    const results = {};
    const uncachedRepos = [];
    const cachedResults = {};

    // Check cache first
    repos.forEach(repo => {
      const cached = this.getCachedLicense(repo);
      if (cached) {
        cachedResults[repo] = cached;
      } else {
        uncachedRepos.push(repo);
      }
    });

    // Return cached results immediately
    Object.assign(results, cachedResults);

    // If no uncached repos, return early
    if (uncachedRepos.length === 0) {
      return results;
    }

    // Batch fetch uncached repos
    const batchResults = await this.fetchBatchFromAPI(uncachedRepos, apiUrl, idToken);
    Object.assign(results, batchResults);

    return results;
  }

  // Fetch license data in batches from the API
  async fetchBatchFromAPI(repos, apiUrl, idToken) {
    const results = {};
    const batchSize = 20; // Use larger batches with the new batch endpoint

    for (let i = 0; i < repos.length; i += batchSize) {
      const batch = repos.slice(i, i + batchSize);
      
      try {
        // Use the new batch endpoint
        const batchResponse = await fetch(`${apiUrl}/batch-licenses?repos=${batch.map(encodeURIComponent).join(',')}`, {
          method: 'GET',
          headers: { Authorization: idToken }
        });

        if (batchResponse.ok) {
          const batchData = await batchResponse.json();
          
          // Process batch results - FIXED: Use Promise.all to wait for async operations
          const processingPromises = Object.entries(batchData.licenses).map(async ([repo, data]) => {
            if (data.yaml) {
              try {
                // Parse the YAML and validate license
                const licenseData = await this.parseLicenseFromYaml(data.yaml, repo, apiUrl, idToken);
                this.setCachedLicense(repo, licenseData, data.etag);
                return [repo, licenseData];
              } catch (error) {
                console.error(`Error parsing license for ${repo}:`, error);
                return [repo, { status: 'error', error: error.message }];
              }
            } else {
              return [repo, { status: 'error', error: data.error || 'Failed to fetch' }];
            }
          });

          // Wait for all processing to complete
          const processedResults = await Promise.all(processingPromises);
          
          // Add results to the main results object
          processedResults.forEach(([repo, data]) => {
            results[repo] = data;
          });
        } else {
          // Check for rate limiting error
          const errorText = await batchResponse.text();
          if (errorText.includes('API rate limit exceeded') || batchResponse.status === 403) {
            // Mark all repos in this batch as rate limited
            batch.forEach(repo => {
              results[repo] = { status: 'rate_limited', error: 'GitHub API rate limit exceeded. Please try again later.' };
            });
          } else {
            // Fallback to individual requests if batch fails for other reasons
            console.warn('Batch endpoint failed, falling back to individual requests');
            const batchPromises = batch.map(repo => 
              this.fetchSingleLicense(repo, apiUrl, idToken)
            );

            const batchResults = await Promise.allSettled(batchPromises);
            
            batchResults.forEach((result, index) => {
              const repo = batch[index];
              if (result.status === 'fulfilled') {
                results[repo] = result.value;
              } else {
                console.error(`Failed to fetch license for ${repo}:`, result.reason);
                results[repo] = { status: 'error', error: result.reason.message };
              }
            });
          }
        }
      } catch (error) {
        console.error(`Error in batch ${Math.floor(i / batchSize) + 1}:`, error);
        // Mark all repos in this batch as error
        batch.forEach(repo => {
          results[repo] = { status: 'error', error: error.message };
        });
      }

      // Add small delay between batches to be respectful to the API
      if (i + batchSize < repos.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return results;
  }

  // Fetch single license from our API
  async fetchSingleLicense(repo, apiUrl, idToken) {
    const etag = this.etags.get(repo);
    const headers = { Authorization: idToken };
    
    if (etag) {
      headers['If-None-Match'] = etag;
    }

    try {
      const response = await fetch(`${apiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers
      });

      // Handle 304 Not Modified
      if (response.status === 304) {
        const cached = this.cache.get(repo);
        return cached ? cached.data : { status: 'none' };
      }

      // Store new ETag if provided
      const newEtag = response.headers.get('ETag');
      if (newEtag) {
        this.etags.set(repo, newEtag);
      }

      if (response.ok) {
        const yamlText = await response.text();
        const licenseData = await this.parseLicenseFromYaml(yamlText, repo, apiUrl, idToken);
        this.setCachedLicense(repo, licenseData, newEtag);
        return licenseData;
      } else {
        // Check for rate limiting error
        const errorText = await response.text();
        if (errorText.includes('API rate limit exceeded') || response.status === 403) {
          return { status: 'rate_limited', error: 'GitHub API rate limit exceeded. Please try again later.' };
        }
        return { status: 'none' };
      }
    } catch (error) {
      console.error(`Error fetching license for ${repo}:`, error);
      // Check if it's a rate limiting error
      if (error.message && error.message.includes('rate limit')) {
        return { status: 'rate_limited', error: 'GitHub API rate limit exceeded. Please try again later.' };
      }
      return { status: 'error', error: error.message };
    }
  }

  // Parse license + basic configuration information from YAML
  async parseLicenseFromYaml(yamlText, repo, apiUrl, idToken) {
    try {
      const yaml = await import('js-yaml');
      const doc = yaml.default.load(yamlText);
      const values = doc?.spec?.source?.helm?.valuesObject || {};
      const license = values.license;

      let hostValue = values.host || '';
      if (hostValue.endsWith('.nip.io')) {
        hostValue = hostValue.replace('.nip.io', '');
      }

      const basicConfig = {
        target_revision: doc?.spec?.source?.targetRevision || null,
        name: values.name || null,
        jurisdiction: values.jurisdiction || null,
        tz: values.tz || null,
        stage: values.stage || null,
        device: values.device || null,
        host: hostValue || null,
        dicom_aet: values.services?.orthanc?.dicomAet || null,
      };

      if (!license) {
        return { status: 'none', ...basicConfig };
      }

      // Validate the license using our own API
      const validateResponse = await fetch(`${apiUrl}/validate-license`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: idToken
        },
        body: JSON.stringify({ license })
      });

      if (validateResponse.ok) {
        const validateData = await validateResponse.json();
        if (validateData.valid) {
          // Always trust the server: valid means valid
          return {
            status: 'valid',
            expiresAt: validateData.expires_at,
            issuedTo: validateData.issued_to,
            license: license,
            ...basicConfig,
          };
        } else {
          // Always trust the server: not valid means expired
          return {
            status: 'expired',
            expiresAt: validateData.expires_at,
            issuedTo: validateData.issued_to,
            license: license,
            ...basicConfig,
          };
        }
      } else {
        // Only fallback to client-side check if server is down
        if (validateResponse.status === 502) {
          console.warn('License validation server unavailable, using client-side validation');
          return this.validateLicenseClientSide(license);
        }
        const errorText = await validateResponse.text();
        return { status: 'error', error: `License validation failed: ${errorText}` };
      }
    } catch (error) {
      console.error(`Error parsing license for ${repo}:`, error);
      return { status: 'error', error: error.message };
    }
  }

  // Refresh license in background
  refreshLicenseInBackground(repo) {
    // Prevent duplicate background requests
    if (this.pendingRequests.has(repo)) {
      return;
    }

    this.pendingRequests.set(repo, true);
    
    // This would be called with proper context in the actual implementation
    setTimeout(() => {
      this.pendingRequests.delete(repo);
    }, 5000); // Clean up after 5 seconds
  }

  // Start background refresh timer
  startBackgroundRefresh(callback) {
    if (this.backgroundRefreshTimer) {
      clearInterval(this.backgroundRefreshTimer);
    }

    this.backgroundRefreshTimer = setInterval(() => {
      const now = Date.now();
      const expiredRepos = [];

      this.cache.forEach((data, repo) => {
        if (now - data.timestamp > this.refreshInterval) {
          expiredRepos.push(repo);
        }
      });

      if (expiredRepos.length > 0 && callback) {
        callback(expiredRepos);
      }
    }, this.refreshInterval);
  }

  // Stop background refresh
  stopBackgroundRefresh() {
    if (this.backgroundRefreshTimer) {
      clearInterval(this.backgroundRefreshTimer);
      this.backgroundRefreshTimer = null;
    }
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
    this.etags.clear();
    this.lastRefresh.clear();
  }

  // Get cache statistics
  getCacheStats() {
    return {
      totalCached: this.cache.size,
      totalEtags: this.etags.size,
      pendingRequests: this.pendingRequests.size
    };
  }

  // Client-side license validation fallback
  validateLicenseClientSide(licenseToken) {
    try {
      // Simple JWT decode without verification (for fallback only)
      const parts = licenseToken.split('.');
      if (parts.length !== 3) {
        return { status: 'error', error: 'Invalid license format' };
      }

      const payload = JSON.parse(atob(parts[1]));
      
      // Check if license has expired
      if (payload.expires_at) {
        const expirationTime = new Date(payload.expires_at).getTime();
        const currentTime = Date.now();
        
        if (currentTime > expirationTime) {
          return {
            status: 'expired',
            expiresAt: payload.expires_at,
            issuedTo: payload.issued_to,
            license: licenseToken
          };
        }
      }
      
      return {
        status: 'valid',
        expiresAt: payload.expires_at,
        issuedTo: payload.issued_to,
        license: licenseToken
      };
    } catch (error) {
      console.error('Client-side validation error:', error);
      return { status: 'error', error: 'License validation failed' };
    }
  }
}

// Create singleton instance
const licenseCache = new LicenseCache();

export default licenseCache; 